import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api, PO_STATUSES, CURRENCIES } from '../api.js';
import AutocompleteInput from '../components/AutocompleteInput.jsx';
import VendorPicker from '../components/VendorPicker.jsx';

function emptyForm() {
  return {
    concessionIds: [],
    departmentId: '',
    vendor: '',
    vendorId: null,
    itemName: '',
    quantity: 1,
    purchaseType: '',
    requestedForName: '',
    approverId: '',
    deliveryDate: '',
    deliveryTbd: false,
    amountBeforeTax: '',
    amountAfterTax: '',
    amountUnknown: false,
    currency: 'CAD',
    status: 'ORDERED',
    isOffBudget: false,
    note: '',
  };
}

export default function POForm() {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();
  const editing = !!params.id;

  const [concessions, setConcessions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [files, setFiles] = useState([]); // new files to upload after PO is created/updated
  const [invoices, setInvoices] = useState([]); // existing attachments (edit mode)
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/api/concessions'),
      api.get('/api/departments'),
      api.get('/api/users'),
    ]).then(([c, d, u]) => {
      setConcessions(c.data.concessions);
      setDepartments(d.data.departments);
      setUsers(u.data.users);
    }).catch((err) => toast.error(err.message));
  }, []);

  useEffect(() => {
    if (!editing) return;
    api.get(`/api/purchase-orders/${params.id}`)
      .then((res) => {
        const po = res.data.purchaseOrder;
        setForm({
          concessionIds: po.concessions.map((c) => c.id),
          departmentId: po.department?.id || '',
          vendor: po.vendor || '',
          vendorId: po.vendorId || null,
          itemName: po.itemName || '',
          // description is deliberately absent: leaving it out of the payload makes
          // the server skip the field, so a historic PO keeps its value through an edit.
          quantity: po.quantity || 1,
          purchaseType: po.purchaseType || '',
          requestedForName: po.requestedForName || '',
          approverId: po.approver?.id || '',
          deliveryDate: po.deliveryDate ? po.deliveryDate.slice(0, 10) : '',
          deliveryTbd: !!po.deliveryTbd,
          amountBeforeTax: po.amountBeforeTax ?? '',
          amountAfterTax: po.amountAfterTax ?? '',
          amountUnknown: !!po.amountUnknown,
          currency: po.currency || 'CAD',
          status: po.status,
          isOffBudget: !!po.isOffBudget,
          note: po.note || '',
        });
        setInvoices(po.invoices || []);
      })
      .catch((err) => toast.error(err.message));
  }, [editing, params.id]);

  const removeAttachment = async (invId) => {
    if (!confirm(t('po.attachments.confirmDelete'))) return;
    try {
      await api.del(`/api/purchase-orders/${params.id}/attachments/${invId}`);
      setInvoices((p) => p.filter((inv) => inv.id !== invId));
      toast.success(t('common.deleted'));
    } catch (err) { toast.error(err.message); }
  };

  const toggleConcession = (id) => {
    setForm((f) => ({
      ...f,
      concessionIds: f.concessionIds.includes(id)
        ? f.concessionIds.filter((x) => x !== id)
        : [...f.concessionIds, id],
    }));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    setFiles((prev) => [...prev, ...dropped]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.concessionIds.length === 0) {
      return toast.error(t('po.form.atLeastOneConcession'));
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity) || 1,
        amountBeforeTax: form.amountBeforeTax === '' ? null : Number(form.amountBeforeTax),
        amountAfterTax: form.amountAfterTax === '' ? null : Number(form.amountAfterTax),
        deliveryDate: form.deliveryDate ? new Date(form.deliveryDate).toISOString() : null,
        departmentId: form.departmentId || null,
        approverId: form.approverId || null,
        vendorId: form.vendorId || null,
      };
      const result = editing
        ? await api.put(`/api/purchase-orders/${params.id}`, payload)
        : await api.post('/api/purchase-orders', payload);
      const po = result.data.purchaseOrder;
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach((f) => fd.append('files', f));
        await api.upload(`/api/purchase-orders/${po.id}/attachments`, fd);
      }
      toast.success(editing ? t('po.form.updated') : t('po.form.created'));
      navigate(`/purchase-orders/${po.id}`, { replace: true });
    } catch (err) {
      toast.error(err.message || t('po.form.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-lg font-semibold mb-4">{editing ? t('po.form.editTitle') : t('po.form.newTitle')}</h1>
      <form onSubmit={submit} className="space-y-5">
        <div className="card p-4 space-y-4">
          <div>
            <label className="label">{t('po.form.concessions')} <span className="text-rose-400">*</span></label>
            <div className="flex flex-wrap gap-1">
              {concessions.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => toggleConcession(c.id)}
                  className={`text-xs px-2 py-1 rounded border ${
                    form.concessionIds.includes(c.id)
                      ? 'bg-cyan-900/30 border-cyan-700 text-cyan-200'
                      : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                  }`}
                >
                  {c.code} · {c.name}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">{t('po.form.department')}</label>
              <select value={form.departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })} className="input">
                <option value="">—</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">{t('po.form.vendor')} <span className="text-rose-400">*</span></label>
              <VendorPicker
                value={form.vendor}
                vendorId={form.vendorId}
                onChange={(vendor, vendorId) => setForm({ ...form, vendor, vendorId })}
                required
              />
            </div>
          </div>
        </div>

        <div className="card p-4 space-y-4">
          <div>
            <label className="label">{t('po.form.itemName')} <span className="text-rose-400">*</span></label>
            <input required className="input" value={form.itemName} onChange={(e) => setForm({ ...form, itemName: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">{t('po.form.quantity')}</label>
              <input type="number" min={1} className="input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="label">{t('po.form.purchaseType')}</label>
              <AutocompleteInput
                endpoint="purchase-types"
                value={form.purchaseType}
                onChange={(v) => setForm({ ...form, purchaseType: v })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">{t('po.form.requestedFor')}</label>
              <AutocompleteInput
                endpoint="requested-for"
                value={form.requestedForName}
                onChange={(v) => setForm({ ...form, requestedForName: v })}
              />
            </div>
            <div>
              <label className="label">{t('po.form.approver')}</label>
              <select value={form.approverId} onChange={(e) => setForm({ ...form, approverId: e.target.value })} className="input">
                <option value="">—</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="card p-4 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">{t('po.form.amountBeforeTax')}</label>
              <input type="number" step="0.01" min={0} disabled={form.amountUnknown} className="input" value={form.amountBeforeTax} onChange={(e) => setForm({ ...form, amountBeforeTax: e.target.value })} />
            </div>
            <div>
              <label className="label">{t('po.form.amountAfterTax')}</label>
              <input type="number" step="0.01" min={0} disabled={form.amountUnknown} className="input" value={form.amountAfterTax} onChange={(e) => setForm({ ...form, amountAfterTax: e.target.value })} />
            </div>
            <div>
              <label className="label">{t('po.form.currency')}</label>
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="input">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="text-xs text-zinc-400 flex items-center gap-2">
              <input type="checkbox" checked={form.amountUnknown} onChange={(e) => setForm({ ...form, amountUnknown: e.target.checked })} />
              {t('po.form.amountUnknown')}
            </label>
            <label className="text-xs text-zinc-400 flex items-center gap-2">
              <input type="checkbox" checked={form.isOffBudget} onChange={(e) => setForm({ ...form, isOffBudget: e.target.checked })} />
              {t('po.form.offBudget')}
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">{t('po.form.deliveryDate')}</label>
              <input type="date" disabled={form.deliveryTbd} className="input" value={form.deliveryDate} onChange={(e) => setForm({ ...form, deliveryDate: e.target.value })} />
            </div>
            <div className="flex items-end pb-1.5">
              <label className="text-xs text-zinc-400 flex items-center gap-2">
                <input type="checkbox" checked={form.deliveryTbd} onChange={(e) => setForm({ ...form, deliveryTbd: e.target.checked })} />
                {t('po.form.deliveryTbd')}
              </label>
            </div>
            <div>
              <label className="label">{t('po.form.status')}</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input">
                {PO_STATUSES.map((s) => <option key={s} value={s}>{t(`po.status.${s}`)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">{t('po.form.note')}</label>
            <textarea rows={2} className="input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>

        <div className="card p-4">
          <label className="label">{t('po.form.attachments')}</label>
          {invoices.length > 0 && (
            <ul className="mb-3 space-y-1">
              {invoices.map((inv) => (
                <li key={inv.id} className="flex items-center justify-between text-xs bg-zinc-900/50 rounded px-2 py-1.5">
                  <span className="shrink-0 mr-2 px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 text-[10px] uppercase tracking-wide">
                    {t(`po.invoiceTypes.${inv.type || 'FACTURE'}`)}
                  </span>
                  <a href={`/api/purchase-orders/${params.id}/attachments/${inv.id}/download`} className="text-cyan-400 hover:underline truncate flex-1" target="_blank" rel="noreferrer">
                    {inv.filename}
                  </a>
                  <span className="text-zinc-600 ml-2">{(inv.sizeBytes / 1024).toFixed(1)} KB</span>
                  <button type="button" onClick={() => removeAttachment(inv.id)} className="ml-3 text-zinc-600 hover:text-rose-400">×</button>
                </li>
              ))}
            </ul>
          )}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`rounded-md border-2 border-dashed p-6 text-center text-sm transition-colors ${
              dragOver ? 'border-cyan-500 bg-cyan-900/10' : 'border-zinc-800 bg-zinc-900/30'
            }`}
          >
            <p className="text-zinc-400">{t('po.form.dropFiles')}</p>
            <label className="inline-block mt-2">
              <span className="btn-ghost cursor-pointer">{t('po.form.browse')}</span>
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => setFiles((p) => [...p, ...Array.from(e.target.files || [])])}
              />
            </label>
          </div>
          {files.length > 0 && (
            <ul className="mt-3 space-y-1">
              {files.map((f, i) => (
                <li key={i} className="flex items-center justify-between text-xs text-zinc-400 bg-zinc-900 rounded px-2 py-1">
                  <span className="truncate">{f.name} <span className="text-zinc-600">({(f.size / 1024).toFixed(1)} KB)</span></span>
                  <button type="button" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-zinc-300">×</button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate(-1)} className="btn-ghost">{t('common.cancel')}</button>
          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? t('common.saving') : (editing ? t('common.save') : t('po.form.create'))}
          </button>
        </div>
      </form>
    </div>
  );
}
